/*---------------------------------------------------------------------------------------------
* Copyright (c) Bentley Systems, Incorporated. All rights reserved.
* See LICENSE.md in the project root for license terms and full copyright notice.
*--------------------------------------------------------------------------------------------*/
import React, { FunctionComponent, useCallback } from "react";
import { SandboxError } from "../../sandbox-router/SandboxError";
import { Anchor } from "@itwin/itwinui-react";
import { AnchorProps } from "./Link";
import { FocusMode, parseFocusModeString } from "@sandbox/code-editor";
import { tryParseInt } from "./helpers";

export interface RelativeLinkProps extends AnchorProps {
  //onClick: (sandbox?: string, file?: { file: string, start?: number, end?: number }) => void;
  onClick: (filePath: string, start?: number, end?: number, focusMode?: FocusMode) => void;
  onError: (error: SandboxError) => void;
}

export const RelativeLink: FunctionComponent<RelativeLinkProps> = ({ href, onClick, onError, children }) => {

  const onHrefClick = useCallback((event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    event.stopPropagation();

    if (!href)
      return;

    try {
      href = href.startsWith(".") ? href.slice(1) : href;
      href = href.includes("?") ? href : `${href}?`;

      const paramsSubstr = href.indexOf("?");
      const urlSearchParams = new URLSearchParams(href.substring(paramsSubstr));
      const filePath = href.slice(0, paramsSubstr);
      const start = tryParseInt(urlSearchParams.get("start"));
      const end = tryParseInt(urlSearchParams.get("end"));
      const focusMode = parseFocusModeString(urlSearchParams.get("focusMode"));
      onClick(filePath, start, end, focusMode);
    } catch (error: any) {
      onError(error);
    }
  }, [href, onClick, onError]);

  return (<Anchor onClick={onHrefClick}>{children}</Anchor>);
};
